import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef, useMemo } from "react";
import { FlowbiteSidebar } from "@/components/FlowbiteSidebar";
import { Button } from "@/components/ui/button";
import { MessageCircle, LogOut, Wallet, ChevronDown, Menu, Crown, User, Users, Building2 } from "lucide-react";
import { useQuery, useApolloClient } from "@apollo/client/react";
import { GET_CURRENT_USER, GET_SUBSCRIPTION_PLAN } from "@/graphql/queries";
import { useErrorTrackingContext } from "@/contexts/ErrorTrackingContext";
import { useAuthError, categorizeAuthError } from "@/contexts/AuthErrorContext";
import MaintenanceScreen from "@/components/MaintenanceScreen";
import AuthErrorScreen from "@/components/AuthErrorScreen";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import SupportDialog from "@/components/SupportDialog";
import { OrganizationSwitcher } from "@/components/OrganizationSwitcher";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AvatarImageWithRetry } from "@/components/ui/avatar-image-with-retry";
import { toast } from "sonner";
import { clearImpersonationSession, getImpersonationSession, getScopedStorageItem, removeScopedStorageItem, setScopedStorageItem } from "@/lib/session-utils";
import { clearTokens } from "@/lib/token-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const { isInErrorState, reset } = useErrorTrackingContext();
  const { authError, setAuthError, clearAuthError } = useAuthError();
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const previousCollapsedState = useRef(false);
  const sidebarCollapsedRef = useRef(sidebarCollapsed);
  sidebarCollapsedRef.current = sidebarCollapsed;

  // Auto-collapse sidebar on Settings page, restore when leaving
  const isSettingsPage = location.pathname === '/settings' || location.pathname.startsWith('/settings');

  useEffect(() => {
    if (isSettingsPage) {
      // Save current state before collapsing
      previousCollapsedState.current = sidebarCollapsedRef.current;
      setSidebarCollapsed(true);
    } else {
      // Restore previous state when leaving settings
      setSidebarCollapsed(previousCollapsedState.current);
    }
  }, [isSettingsPage]);

  // Proactively refresh tokens before they expire to prevent "authentication required" errors
  useTokenRefresh();

  // Fetch current user data to get organization name
  const { data: userData, error: userError, loading: userLoading } = useQuery<{ me?: { name?: string; email?: string; avatarURL?: string; organizations?: { id: string; name: string; role: string; isPersonal?: boolean }[] } | null }>(GET_CURRENT_USER, {
    // Don't show loading state, just fetch the data
    fetchPolicy: 'cache-and-network',
  });

  // Fetch subscription
  const { data: subscriptionData } = useQuery<{ subscription?: { plan?: string } | null }>(GET_SUBSCRIPTION_PLAN, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const currentUser = userData?.me || {};
  const subscription = subscriptionData?.subscription;
  const currentPlan = subscription?.plan || 'PERSONAL';
  const organizations = useMemo(() => userData?.me?.organizations || [], [userData?.me?.organizations]);
  const impersonationSession = getImpersonationSession();
  
  // Use shared hook to get current organization consistently
  const { currentOrganizationId } = useCurrentOrganization(organizations);

  // Handle case when user is kicked from organization
  useEffect(() => {
    // Only run when we have finished loading and have user data
    if (userLoading || !userData) return;
    
    const storedOrgId = getScopedStorageItem('currentOrganizationId');
    
    if (organizations.length === 0) {
      toast.error('You no longer belong to any organization. Please sign in again.');
      clearTokens();
      removeScopedStorageItem('currentOrganizationId');
      apolloClient.stop();
      apolloClient.clearStore().finally(() => {
        navigate('/login');
      });
      return;
    }
    
    if (storedOrgId && !organizations.some((org: { id: string }) => org.id === storedOrgId)) {
      const newOrg = organizations[0];
      toast.info(`You were removed from a workspace. Switched to "${newOrg?.name || 'your workspace'}".`);
      if (newOrg?.id) {
        setScopedStorageItem('currentOrganizationId', newOrg.id);
      }
      window.location.reload();
    }
  }, [organizations, userData, userLoading, apolloClient, navigate]);

  // Plan display config
  const planConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string; label: string }> = {
    PERSONAL: { icon: <User className="w-3 h-3" />, color: 'text-slate-400', bgColor: 'bg-slate-500/10', label: 'Personal' },
    PRO: { icon: <Crown className="w-3 h-3" />, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Pro' },
    STUDIO: { icon: <Users className="w-3 h-3" />, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Studio' },
    ENTERPRISE: { icon: <Building2 className="w-3 h-3" />, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Enterprise' },
  };
  const plan = planConfig[currentPlan] || planConfig.PERSONAL;

  // Generate initials from user name or email
  const getInitials = (nameOrEmail: string) => {
    if (!nameOrEmail) return "U";
    if (nameOrEmail.includes('@')) {
      return nameOrEmail[0].toUpperCase();
    }
    return nameOrEmail
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const userName = currentUser.name || currentUser.email || "User";
  const userEmail = currentUser.email || "";
  const userInitials = getInitials(userName);

  const handleSignOut = async () => {
    try {
      clearTokens();
      apolloClient.stop();
      await apolloClient.clearStore();
      navigate('/login');
    } catch (error) {
      console.error('Error during sign out:', error);
      clearTokens();
      navigate('/login');
    }
  };

  const handleStopImpersonation = async () => {
    clearImpersonationSession();
    apolloClient.stop();
    await apolloClient.clearStore();
    window.location.href = localStorage.getItem('authToken') ? '/' : '/login';
  };

  // Handle authentication errors from the me query
  useEffect(() => {
    if (userError) {
      console.error('[DashboardLayout] Error fetching current user:', userError);
      const errorMessage = userError.message;
      
      // Check if this is a categorizable auth error
      const categorizedError = categorizeAuthError(errorMessage);
      
      if (categorizedError) {
        // Show the auth error screen instead of redirecting
        console.warn('[DashboardLayout] Auth error detected, showing error screen:', categorizedError.type);
        setAuthError(categorizedError);
        return;
      }
      
      // Fallback for other authentication errors
      const lowerMessage = errorMessage.toLowerCase();
      if (
        lowerMessage.includes('authentication') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('token')
      ) {
        console.warn('[DashboardLayout] Authentication error, redirecting to login');
        clearTokens();
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
      }
    }
  }, [userError, navigate, location.pathname, setAuthError]);


  // Show auth error screen if there's an authentication/authorization error
  if (authError) {
    return <AuthErrorScreen error={authError} onRetry={clearAuthError} />;
  }

  // Show maintenance screen if in error state
  if (isInErrorState) {
    return <MaintenanceScreen onRetry={reset} />;
  }

  return (
    <div className="h-screen flex w-full bg-background overflow-hidden">
      <FlowbiteSidebar 
        collapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {impersonationSession ? (
          <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Impersonating {impersonationSession.user.name || impersonationSession.user.email}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {impersonationSession.user.email} in {impersonationSession.organization.name}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleStopImpersonation} className="shrink-0">
              Stop impersonating
            </Button>
          </div>
        ) : null}
        {/* Header */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm z-40 shrink-0 flex-none">
          <div className="flex items-center justify-between px-6 h-[73px]">
            <div className="flex items-center gap-4 min-w-0">
              <Button 
                variant="ghost" 
                size="icon"
                className="shrink-0 md:hidden"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <Link
                to="/"
                className="flex items-center gap-3 shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <img src="/Logo White Transparent.png" alt="FlowLabs" className="h-8 w-auto" />
                <div className="font-heading font-bold text-xl tracking-tight">
                  FLOWLABS
                </div>
              </Link>
              {organizations.length > 0 && (
                <div className="ml-4 pl-4 border-l border-border shrink-0" data-tour-id="dashboard-organization-switcher">
                  <OrganizationSwitcher
                    organizations={organizations}
                    currentOrganizationId={currentOrganizationId}
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-4 shrink-0">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSupportDialogOpen(true)}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
              
              {/* User Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 px-2 py-1.5 h-auto hover:bg-muted/60"
                  >
                    <Avatar className="w-8 h-8">
                      {currentUser.avatarURL && currentUser.avatarURL.trim() !== "" ? (
                        <AvatarImageWithRetry 
                          src={currentUser.avatarURL} 
                          alt={userName}
                          maxRetries={1}
                          retryDelay={5000}
                          enableSmartRetry={true}
                        />
                      ) : null}
                      <AvatarFallback className="bg-gradient-professional text-white text-sm font-semibold">
                        {userLoading ? "..." : userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm hidden md:inline-block max-w-[120px] truncate">
                      {userLoading ? "Loading..." : userName}
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {/* User Info Header */}
                  <div className="px-3 py-3 border-b border-border">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        {currentUser.avatarURL && currentUser.avatarURL.trim() !== "" ? (
                          <AvatarImageWithRetry 
                            src={currentUser.avatarURL} 
                            alt={userName}
                            maxRetries={1}
                            retryDelay={5000}
                            enableSmartRetry={true}
                          />
                        ) : null}
                        <AvatarFallback className="bg-gradient-professional text-white text-sm font-semibold">
                          {userInitials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{userName}</p>
                        <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                      </div>
                    </div>
                    {/* Plan Badge */}
                    <div 
                      className={`mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md ${plan.bgColor} ${plan.color} text-xs font-medium w-fit cursor-pointer hover:opacity-80`}
                      onClick={() => navigate('/settings?section=credits&creditsTab=manage-plan')}
                    >
                      {plan.icon}
                      <span>{plan.label} Plan</span>
                    </div>
                  </div>
                  
                  {/* Credits */}
                  <DropdownMenuItem
                    onClick={() => navigate('/settings?section=credits')}
                    className="cursor-pointer py-3"
                  >
                    <Wallet className="w-4 h-4 mr-3" />
                    <span>Credits</span>
                  </DropdownMenuItem>

                  {/* Members */}
                  <DropdownMenuItem
                    onClick={() => navigate('/settings?section=members')}
                    className="cursor-pointer py-3"
                  >
                    <Users className="w-4 h-4 mr-3" />
                    <span>Members</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                  
                  {/* Logout */}
                  <DropdownMenuItem 
                    onClick={handleSignOut}
                    className="cursor-pointer py-3 text-destructive focus:text-destructive"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      
      {/* Support Dialog */}
      <SupportDialog 
        open={supportDialogOpen} 
        onOpenChange={setSupportDialogOpen} 
      />
    </div>
  );
};

export default DashboardLayout;
